import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  Copy,
  Check,
  Phone,
  BookOpen,
  Send
} from 'lucide-react';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import Button from '../components/Button';
import { resourcesData } from '../data/resourcesData';

export default function ResourceDetail({ onOpenConsultation }) {
  const { resourceId } = useParams();
  const [copied, setCopied] = useState(false);

  const article = resourcesData.find((a) => a.slug === resourceId || a.id === resourceId);

  if (!article) {
    return <Navigate to="/resources" replace />;
  }

  const relatedArticles = resourcesData.filter((a) => a.id !== article.id).slice(0, 3);

  const handleShare = async () => {
    const shareData = {
      title: article.title,
      text: article.shortDescription,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Helper to render bold text inside markdown lines
  const parseBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-[#123B5D]">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Structured content renderer
  const renderFormattedContent = (content) => {
    if (!content) return null;
    const lines = content.trim().split('\n');
    const elements = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      if (trimmed.startsWith('### ')) {
        elements.push(
          <h3 key={index} className="font-serif text-2xl sm:text-3xl text-[#123B5D] font-normal mt-10 mb-4 pt-4 border-t border-[#D9E8E5] first:mt-0 first:pt-0 first:border-0">
            {trimmed.replace('### ', '')}
          </h3>
        );
      } else if (trimmed.startsWith('#### ')) {
        elements.push(
          <h4 key={index} className="font-serif text-xl text-[#0F766E] font-normal mt-7 mb-3">
            {trimmed.replace('#### ', '')}
          </h4>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const text = trimmed.replace(/^[-*]\s+/, '');
        elements.push(
          <div key={index} className="flex items-start gap-3 my-2.5 text-[#475569] text-base sm:text-lg leading-relaxed">
            <span className="w-2 h-2 rounded-full bg-[#0F766E] flex-shrink-0 mt-2.5"></span>
            <span>{parseBold(text)}</span>
          </div>
        );
      } else if (/^\d+\.\s+/.test(trimmed)) {
        const match = trimmed.match(/^(\d+)\.\s+(.*)/);
        elements.push(
          <div key={index} className="flex items-start gap-4 my-4 p-5 rounded-2xl bg-[#F5FAF9] border border-[#D9E8E5] shadow-sm">
            <span className="w-8 h-8 rounded-xl bg-[#123B5D] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
              {match[1]}
            </span>
            <div className="text-[#334155] text-base sm:text-lg leading-relaxed pt-0.5">
              {parseBold(match[2])}
            </div>
          </div>
        );
      } else {
        elements.push(
          <p key={index} className="text-[#475569] text-base sm:text-lg leading-relaxed mb-5 font-normal">
            {parseBold(trimmed)}
          </p>
        );
      }
    });

    return elements;
  };

  return (
    <div className="bg-canvas">
      <SEO
        title={`${article.title} | UMED Clinical Insights`}
        description={article.shortDescription}
      />

      {/* Hero Header */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-24 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#2A9D8F] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Articles & Guides</span>
            </Link>

            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-semibold text-white transition-all backdrop-blur-sm"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-[#2A9D8F]" /> : <Share2 className="w-3.5 h-3.5 text-[#2A9D8F]" />}
              <span>{copied ? 'Link Copied!' : 'Share Article'}</span>
            </button>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <BookOpen className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                {article.category}
              </span>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-normal leading-tight">
              {article.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-[#D9E8E5] pt-6 border-t border-white/10">
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4 text-[#2A9D8F]" />
                <span>By <strong>{article.author}</strong> ({article.authorRole})</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[#2A9D8F]" />
                <span>{article.date}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-[#2A9D8F]" />
                <span>{article.readTime}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main 2-Column Split Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Image, Author Details, Share Button & Helpline Card */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
              
              {/* Featured Image */}
              <div className="rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] aspect-[4/3] bg-[#123B5D] relative group">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#123B5D]/60 via-transparent to-transparent"></div>
              </div>

              {/* Author & Article Meta Info Card */}
              <div className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5] flex items-center justify-center flex-shrink-0 font-serif font-bold text-lg">
                    {article.author.split(' ').find(w => !w.includes('.'))?.[0] || 'S'}
                  </div>
                  <div>
                    <h4 className="font-serif text-base text-[#123B5D] font-semibold">
                      {article.author}
                    </h4>
                    <span className="text-xs text-[#64748B] block">
                      {article.authorRole}
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t border-[#D9E8E5] flex items-center justify-between text-xs text-[#64748B]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-[#0F766E]" />
                    <span>{article.date}</span>
                  </span>
                  <span className="flex items-center gap-1.5 font-medium text-[#123B5D]">
                    <Clock className="w-3.5 h-3.5 text-[#0F766E]" />
                    <span>{article.readTime}</span>
                  </span>
                </div>

                {/* Share Article Action Row */}
                <div className="pt-4 border-t border-[#D9E8E5] space-y-3">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-[#64748B] block">
                    Share This Article
                  </span>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleShare}
                      className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl bg-[#F5FAF9] hover:bg-[#0F766E] text-[#0F766E] hover:text-white border border-[#D9E8E5] text-xs font-semibold transition-all duration-200"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      <span>{copied ? 'Copied!' : 'Copy Link'}</span>
                    </button>

                    <a
                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title + " - " + window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366] text-[#0F766E] hover:text-white border border-[#25D366]/30 text-xs font-semibold transition-all duration-200"
                    >
                      <Send className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Need Guidance Helpline Card */}
              <div className="p-6 rounded-2xl bg-[#123B5D] text-white shadow-card space-y-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0F766E]/20 rounded-full blur-2xl pointer-events-none"></div>
                <div className="relative z-10 space-y-3">
                  <span className="text-[10px] uppercase tracking-widest text-[#2A9D8F] font-bold block">
                    Need Immediate Guidance?
                  </span>
                  <h4 className="font-serif text-lg text-white font-normal leading-snug">
                    Consult Our Psychiatric Care Team
                  </h4>
                  <p className="text-xs text-[#D9E8E5] leading-relaxed">
                    Confidential admissions, psychiatric triage, and rehabilitation guidance available 24/7.
                  </p>

                  <div className="pt-2 flex flex-col sm:flex-row gap-2">
                    <Button onClick={onOpenConsultation} variant="primary" size="sm" className="w-full justify-center">
                      Request Consultation
                    </Button>
                    <a
                      href="tel:+919226999705"
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/20 text-xs font-semibold text-white hover:bg-white/10 transition"
                    >
                      <Phone className="w-3.5 h-3.5 text-[#2A9D8F]" />
                      <span>Call 24/7</span>
                    </a>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Article Summary & Main Content */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* Highlight Executive Summary Quote Box */}
              <div className="p-8 rounded-3xl bg-white border-l-4 border-[#0F766E] border border-[#D9E8E5] shadow-card">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0F766E] block mb-2">
                  KEY TAKEAWAY & OVERVIEW
                </span>
                <p className="text-lg sm:text-xl font-serif text-[#123B5D] leading-relaxed italic">
                  "{article.shortDescription}"
                </p>
              </div>

              {/* Main Body Prose Card */}
              <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9E8E5] shadow-card">
                {renderFormattedContent(article.content)}
              </div>

              {/* Bottom Share & Navigation Footer */}
              <div className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#123B5D] uppercase tracking-wider">
                    Found this article helpful?
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F766E] text-white text-xs font-semibold hover:bg-[#123B5D] transition-colors shadow-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>{copied ? 'Copied to Clipboard!' : 'Share Article'}</span>
                  </button>

                  <Link
                    to="/resources"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F766E] hover:underline"
                  >
                    <span>More Articles</span>
                    <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
                  </Link>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 lg:py-24 bg-[#F5FAF9] border-t border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0F766E] block mb-1">
                RECOMMENDED READING
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#123B5D] font-normal">
                Related Clinical & Family Guides
              </h3>
            </div>
            <Link
              to="/resources"
              className="text-xs font-semibold text-[#0F766E] hover:text-[#123B5D] underline underline-offset-4 hidden sm:block"
            >
              View All Resources
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedArticles.map((rel) => (
              <Link
                key={rel.id}
                to={`/resources/${rel.slug}`}
                className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card hover:shadow-elevated transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
              >
                <div>
                  <div className="h-44 rounded-xl overflow-hidden mb-5 bg-[#123B5D]">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span className="text-[11px] font-bold text-[#0F766E] uppercase tracking-wider block mb-1">
                    {rel.category}
                  </span>
                  <h4 className="font-serif text-lg text-[#123B5D] font-normal group-hover:text-[#0F766E] transition-colors line-clamp-2 leading-snug">
                    {rel.title}
                  </h4>
                  <p className="text-xs text-[#64748B] mt-2 line-clamp-2 leading-relaxed">
                    {rel.shortDescription}
                  </p>
                </div>
                <div className="mt-5 pt-4 border-t border-[#D9E8E5] flex items-center justify-between text-xs text-[#0F766E] font-semibold">
                  <span>Read Article</span>
                  <ArrowLeft className="w-3.5 h-3.5 rotate-180 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
